declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

/**
 * Typed event taxonomy for Primero Dental Clinic conversion measurement.
 *
 * Every event is pushed to the GTM dataLayer. In GTM, create:
 *   - Custom Event triggers matching the event name
 *   - GA4 Event tags that map same-named fields as event parameters
 *
 * Privacy note: NO PII (name, phone, treatment interest) may be sent as parameters.
 */

type ContactMethod = "phone" | "whatsapp" | "email" | "map" | "link";
type CTASource =
  | "hero_cta"
  | "quick_action_bar_desktop_emergency"
  | "quick_action_bar_desktop_booking"
  | "quick_action_bar_mobile_emergency"
  | "quick_action_bar_mobile_booking"
  | "contact_section_info"
  | "contact_section_cta"
  | "floating_widget"
  | "floating_whatsapp_open"
  | "booking_dialog_open"
  | string; // Allow other sources for flexibility

type BookingStep = "step_1_date_time" | "step_2_service" | "step_3_contact_info";

type LeadOutcome = "whatsapp_opened" | "popup_blocked";

type OutboundClickTarget =
  | "google_maps"
  | "social_facebook"
  | "social_instagram"
  | "social_linkedin"
  | "external_link";

/**
 * Union of all typed events with their respective parameters.
 * Call sites cannot invent event names or misspell parameters — TypeScript enforces it.
 */
type AnalyticsEvent =
  | {
      event: "contact_click";
      contact_method: ContactMethod;
      cta_source: CTASource;
    }
  | {
      event: "generate_lead";
      lead_method: "whatsapp_booking";
      lead_outcome: LeadOutcome;
      service: string; // Service slug: "cosmetic", "implants", "orthodontics", etc.
      appointment_date: string; // Formatted date string, e.g. "Monday, August 18, 2024"
      appointment_time: string; // Time slot, e.g. "2:00 PM"
    }
  | {
      event: "booking_open";
    }
  | {
      event: "booking_step";
      step: BookingStep;
    }
  | {
      event: "outbound_click";
      target: OutboundClickTarget;
      url?: string; // Optional, redacted URL if needed
    }
  | {
      event: "cta_click";
      cta_name: string; // e.g., "book_appointment_open", "floating_whatsapp_open"
    }
  | {
      event: "service_page_cta";
      service_slug: string;
      cta_name: string;
    };

/**
 * Pushes a custom event to the GTM dataLayer.
 * SSG-safe: no-ops harmlessly if window is undefined (server render) or dataLayer is not available.
 *
 * @param event - Event type (type-checked to prevent typos)
 * @param params - Event parameters (type-checked per event type)
 */
export function trackEvent(
  event: AnalyticsEvent["event"],
  params: Omit<AnalyticsEvent & { event: typeof event }, "event"> = {}
): void {
  if (typeof window === "undefined") return; // SSG render, no-op
  if (!window.dataLayer) return; // No GTM container configured
  window.dataLayer.push({ event, ...params });
}
